// src/layouts/FeasibilityLayout.jsx
//
// The Feasibility Study is a workflow for one asset: five steps in order,
// ending in the simulation and the reports. The steps are listed in the
// Feasibility workspace sidebar; this layout adds the asset under study, the
// progress through the steps and Back / Next, and renders the existing step
// pages unchanged in between.

import React from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { Alert, Box, Button, LinearProgress, Stack, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AssetSelect from "../components/AssetSelect";
import { FEASIBILITY_STEPS } from "../navigation/sections";
import { useDemo } from "../context/DemoContext";
import { getRegularFeasibilityDisabledMessage } from "../utils/demoAccess";

export default function FeasibilityLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const idx = Math.max(0, FEASIBILITY_STEPS.findIndex((s) => location.pathname === s.to));
  const step = FEASIBILITY_STEPS[idx];
  const prev = FEASIBILITY_STEPS[idx - 1];
  const next = FEASIBILITY_STEPS[idx + 1];
  const { demoProfile, demoError } = useDemo();
  // A background quota refresh must not unmount the active simulation page.
  // Keep displaying the last known access decision until the refresh returns.
  if (!demoProfile && !demoError) return null;
  if (demoError) return <Box sx={{ p: 3 }}><Alert severity="error">Account permissions are unavailable. Please refresh.</Alert></Box>;
  const disabled = getRegularFeasibilityDisabledMessage(demoProfile);
  if (disabled) return <Box sx={{ p: 3 }}><Alert severity="info">{disabled}</Alert></Box>;

  return (
    <Box>
      {/* One slim bar: the asset under study and where we are in the workflow. */}
      <Stack
        direction={{ xs: "column", md: "row" }}
        alignItems={{ md: "center" }}
        spacing={2}
        sx={{ px: { xs: 1.5, md: 3 }, py: 1, borderBottom: "1px solid", borderColor: "divider", bgcolor: "background.paper" }}
      >
        <AssetSelect minWidth={300} maxWidth={560} label="Asset under study" sx={{ width: "auto" }} />
        <Box sx={{ flexGrow: 1, minWidth: 160 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
            Step {idx + 1} of {FEASIBILITY_STEPS.length} · {step.label} — {step.hint}
          </Typography>
          <LinearProgress variant="determinate" value={((idx + 1) / FEASIBILITY_STEPS.length) * 100} sx={{ height: 4, borderRadius: 2 }} />
        </Box>
      </Stack>

      <Outlet />

      <Stack direction="row" justifyContent="space-between" sx={{ px: { xs: 1.5, md: 3 }, mt: 1, mb: 3 }}>
        <Button variant="outlined" startIcon={<ArrowBackIcon />} disabled={!prev} onClick={() => prev && navigate(prev.to)}>
          {prev ? prev.label : "Back"}
        </Button>
        {next && <Button variant="contained" endIcon={<ArrowForwardIcon />} onClick={() => navigate(next.to)}>
          {/* Two nodes, not one interpolated string: the Greek bridge matches
              whole text nodes, so "Next: Weather Data" could never translate. */}
          <span>Next:</span> <span>{next.label}</span>
        </Button>}
      </Stack>
    </Box>
  );
}
